import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.MODE;

/**
 * Minimized user context/error context surface approved for Backoffice diagnostics.
 */
const PUBLIC_CONTEXT_KEYS = new Set(['operation', 'component', 'view', 'path', 'route', 'feature']);

export function stripQueryAndHash(value: string): string {
  return value.split('?')[0].split('#')[0];
}

function sanitizeDiagnosticContext(
  context: Record<string, unknown> | undefined
): Record<string, unknown> {
  if (!context) {
    return {};
  }

  return Object.entries(context).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (!PUBLIC_CONTEXT_KEYS.has(key)) {
      return acc;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      acc[key] = value;
    }
    return acc;
  }, {});
}

export function sanitizeBreadcrumbData(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb {
  if (!breadcrumb.data || typeof breadcrumb.data !== 'object') {
    return breadcrumb;
  }

  const nextData = { ...breadcrumb.data } as Record<string, unknown>;

  (['from', 'to', 'url'] as const).forEach((key) => {
    const value = nextData[key];
    if (typeof value === 'string') {
      nextData[key] = stripQueryAndHash(value);
    }
  });

  return {
    ...breadcrumb,
    data: nextData,
  };
}

export function buildSentryInitOptions(
  overrides: { dsn?: string; environment?: string } = {}
): Sentry.BrowserOptions | null {
  const dsn = overrides.dsn ?? SENTRY_DSN;
  const environment = overrides.environment ?? ENVIRONMENT;

  if (!dsn) {
    return null;
  }

  return {
    dsn,
    environment,
    // Keep breadcrumbs operational and avoid emitting raw ids/queries in navigation payloads.
    beforeBreadcrumb: (breadcrumb) => sanitizeBreadcrumbData(breadcrumb),
    beforeSend: (event) => {
      const request = event.request;
      if (request?.url && typeof request.url === 'string') {
        event.request = {
          ...request,
          url: stripQueryAndHash(request.url),
        };
      }

      if (event.breadcrumbs?.length) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) =>
          sanitizeBreadcrumbData(breadcrumb)
        );
      }

      return event;
    },
  };
}

export function initSentry() {
  const options = buildSentryInitOptions();
  if (!options) {
    return;
  }

  Sentry.init(options);
}

export function captureException(error: Error, context?: Record<string, unknown>) {
  Sentry.withScope((scope) => {
    const safeContext = sanitizeDiagnosticContext(context);
    Object.entries(safeContext).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    Sentry.captureException(error);
  });
}

export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level);
}

export function setUserContext(user: { id: string } | null) {
  if (user) {
    Sentry.setUser({
      id: user.id,
    });
  } else {
    Sentry.setUser(null);
  }
}

export { Sentry };
